import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { isAxiosError } from 'axios'
import { useLocalStorage } from 'usehooks-ts'
import morpheusApi from '@/state/morpheus/morpheus.slice'

const useSelectedOrganisation = () => {
  const { org_id } = useParams<{ org_id: string }>()
  const push = useNavigate()
  const { data, isError, error } = morpheusApi.useGetOrganisationQuery(
    org_id!,
    {
      refetchOnFocus: false,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
      skip: !org_id,
    }
  )

  const [cacheData, setCacheData] = useLocalStorage(
    `org_data_${org_id}`,
    data?.id === org_id ? data : undefined
  )

  useEffect(() => {
    if (!isError) return

    if (
      isAxiosError(error) &&
      error.response?.status &&
      error.response?.status >= 400
    ) {
      push('/')
    }
  }, [isError, error])

  useEffect(() => {
    if (data?.id === org_id) {
      setCacheData(data)
    }
  }, [data, setCacheData])

  return cacheData
}

export const useSelectedOrganisationWithChildren = () => {
  const { org_id } = useParams<{ org_id: string }>()
  const { data: response } = morpheusApi.useGetOrganisationChildrenQuery(
    { orgId: org_id ?? '' },
    {
      skip: !org_id,
    }
  )
  const org = useSelectedOrganisation()
  const children = response?.data ?? []

  return useMemo(() => {
    if (!org) {
      return null
    }
    return { ...org, children }
  }, [org, children])
}

export default useSelectedOrganisation
